import React, { useEffect, useState, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { vi } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { freeKeyService, FreeKey } from '../../services/freeKeyService';
import { useToast } from '../../components/Toast';
import StarRating from '../../components/StarRating';
import './AdminFreeKeysPage.css';

// Register Vietnamese locale
registerLocale('vi', vi);

const AdminFreeKeysPage: React.FC = () => {
  const { showToast } = useToast();
  const [keys, setKeys] = useState<FreeKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [periodTypeFilter, setPeriodTypeFilter] = useState<string>('');
  const [periodValueFilter, setPeriodValueFilter] = useState<string>('');
  const [selectedFilterDate, setSelectedFilterDate] = useState<Date | null>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<FreeKey>>({
    stt: 0,
    quan_doan: '',
    mon_phai: '',
    viral_key: '',
    rpm: '',
    key: '',
    views: 0,
    ngay_dang: '',
    period_type: 'year',
    period_value: '',
    is_active: true,
    display_order: 0,
    prompt: '',
    kenh_doi_thu: '',
  });
  
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPeriodType, setImportPeriodType] = useState<'year' | 'month' | 'week' | 'day'>('year');
  const [importPeriodValue, setImportPeriodValue] = useState<string>('');
  const [importSelectedDate, setImportSelectedDate] = useState<Date | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  useEffect(() => {
    fetchKeys();
  }, [page, search, periodTypeFilter, periodValueFilter, isActiveFilter]);

  // Sync sticky scrollbar with table scroll
  useEffect(() => {
    const wrap = tableWrapRef.current;
    const bar = stickyScrollbarRef.current;
    if (!wrap) return;

    // Set initial width
    const updateWidth = () => {
      const width = wrap.scrollWidth;
      setTableScrollWidth(width);
    };

    // Wait for table to render
    const timer = setTimeout(updateWidth, 100);
    
    if (bar) {
      const syncFromWrap = () => {
        if (bar) {
          bar.scrollLeft = wrap.scrollLeft;
        }
      };
      const syncFromBar = () => {
        if (wrap) {
          wrap.scrollLeft = bar.scrollLeft;
        }
      };

      wrap.addEventListener("scroll", syncFromWrap, { passive: true });
      bar.addEventListener("scroll", syncFromBar, { passive: true });

      const ro = new ResizeObserver(() => {
        updateWidth();
      });
      ro.observe(wrap);

      return () => {
        clearTimeout(timer);
        wrap.removeEventListener("scroll", syncFromWrap);
        if (bar) {
          bar.removeEventListener("scroll", syncFromBar);
        }
        ro.disconnect();
      };
    }

    return () => {
      clearTimeout(timer);
    };
  }, [keys, loading]);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const data = await freeKeyService.list({
        page,
        per_page: perPage,
        search: search || undefined,
        period_type: periodTypeFilter || undefined,
        period_value: periodValueFilter || undefined,
        is_active: isActiveFilter || undefined,
      });
      setKeys(data.keys);
      setTotal(data.total);
    } catch (error: any) {
      showToast('Lỗi khi tải danh sách keys', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await freeKeyService.create(formData);
      showToast('Tạo key thành công', 'success');
      setShowCreateForm(false);
      resetForm();
      fetchKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi tạo key', 'error');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    try {
      await freeKeyService.update(editingId, formData);
      showToast('Cập nhật thành công', 'success');
      setEditingId(null);
      resetForm();
      fetchKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi cập nhật', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bạn có chắc muốn xóa key này?')) return;
    try {
      await freeKeyService.delete(id);
      showToast('Xóa thành công', 'success');
      setSelectedKeys(selectedKeys.filter(keyId => keyId !== id));
      fetchKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedKeys.length === 0) {
      showToast('Vui lòng chọn ít nhất một key để xóa', 'warning');
      return;
    }
    
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedKeys.length} key đã chọn?`)) return;
    
    try {
      const result = await freeKeyService.bulkDelete(selectedKeys);
      showToast(result.message, 'success');
      setSelectedKeys([]);
      setSelectAll(false);
      fetchKeys();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi xóa hàng loạt', 'error');
    }
  };

  const handleSelectKey = (keyId: number) => {
    if (selectedKeys.includes(keyId)) {
      setSelectedKeys(selectedKeys.filter(id => id !== keyId));
    } else {
      setSelectedKeys([...selectedKeys, keyId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedKeys([]);
      setSelectAll(false);
    } else {
      const allKeyIds = keys.map(key => key.id!).filter(id => id !== undefined);
      setSelectedKeys(allKeyIds);
      setSelectAll(true);
    }
  };

  // Update selectAll state when selectedKeys changes
  useEffect(() => {
    if (keys.length > 0) {
      const allSelected = keys.every(key => key.id && selectedKeys.includes(key.id));
      setSelectAll(allSelected && selectedKeys.length === keys.length);
    }
  }, [selectedKeys, keys]);

  const handleEdit = async (id: number) => {
    try {
      const key = await freeKeyService.get(id);
      setFormData({
        stt: key.stt,
        quan_doan: key.quan_doan || '',
        mon_phai: key.mon_phai || '',
        viral_key: key.viral_key || '',
        rpm: key.rpm || '',
        key: key.key,
        views: key.views,
        ngay_dang: key.ngay_dang || '',
        period_type: key.period_type,
        period_value: key.period_value,
        is_active: key.is_active,
        display_order: key.display_order,
        prompt: key.prompt || '',
        kenh_doi_thu: key.kenh_doi_thu || '',
      });
      setEditingId(id);
      setShowCreateForm(true);
    } catch (error: any) {
      showToast('Lỗi khi tải thông tin', 'error');
    }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Store file and show modal to select period
    setSelectedFile(file);
    setShowImportModal(true);
  };

  const confirmImport = async () => {
    if (!importPeriodValue) {
      showToast('Vui lòng nhập Giá trị kỳ', 'error');
      return;
    }

    if (!selectedFile) {
      showToast('Vui lòng chọn file Excel', 'error');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    setShowImportModal(false);
    
    try {
      const result = await freeKeyService.importExcel(
        selectedFile,
        importPeriodType,
        importPeriodValue,
        (progress) => {
          setImportProgress(progress);
        }
      );
      showToast(
        `Import thành công ${result.created} key${result.errors?.length ? `. ${result.errors.length} lỗi` : ''}`,
        result.errors?.length ? 'warning' : 'success'
      );
      if (result.errors && result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
      fetchKeys();
      setImportPeriodValue('');
      setSelectedFile(null);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Lỗi khi import', 'error');
    } finally {
      setImporting(false);
      setImportProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await freeKeyService.exportExcel({
        period_type: periodTypeFilter || undefined,
        period_value: periodValueFilter || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'free_keys.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast('Export thành công', 'success');
    } catch (error: any) {
      showToast('Lỗi khi export', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      stt: 0,
      quan_doan: '',
      mon_phai: '',
      viral_key: '',
      rpm: '',
      key: '',
      views: 0,
      ngay_dang: '',
      period_type: 'year',
      period_value: '',
      is_active: true,
      display_order: 0,
      prompt: '',
      kenh_doi_thu: '',
    });
    setEditingId(null);
  };

  const totalPages = Math.ceil(total / perPage);

  // Helper function để lấy ngày hôm nay theo múi giờ Việt Nam (UTC+7)
  const getVietnamToday = () => {
    const now = new Date();
    // Chuyển sang múi giờ Việt Nam (UTC+7)
    const vietnamTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Ho_Chi_Minh' }));
    return vietnamTime;
  };

  // Helper function to get week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Convert period value to date based on period type
  const periodValueToDate = (value: string, type: 'year' | 'month' | 'week' | 'day'): Date | null => {
    if (!value) return null;
    
    try {
      if (type === 'day') {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      } else if (type === 'month') {
        const [year, month] = value.split('-').map(Number);
        return new Date(year, month - 1, 1);
      } else if (type === 'week') {
        const [yearStr, weekStr] = value.split('-W');
        const year = parseInt(yearStr);
        const week = parseInt(weekStr);
        const jan1 = new Date(year, 0, 1);
        const daysOffset = (jan1.getDay() || 7) - 1;
        const targetDate = new Date(year, 0, 1 + (week - 1) * 7 - daysOffset);
        return targetDate;
      } else if (type === 'year') {
        return new Date(parseInt(value), 0, 1);
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  // Convert date to period value based on period type
  const dateToPeriodValue = (date: Date, type: 'year' | 'month' | 'week' | 'day'): string => {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    if (type === 'day') {
      return `${year}-${month}-${day}`;
    } else if (type === 'month') {
      return `${year}-${month}`;
    } else if (type === 'week') {
      const week = getWeekNumber(date);
      return `${year}-W${String(week).padStart(2, '0')}`;
    } else if (type === 'year') {
      return year.toString();
    }
    return '';
  };

  // DatePicker props based on period type (for filter)
  const getFilterDatePickerProps = () => {
    const baseProps: any = {
      selected: selectedFilterDate,
      onChange: (date: Date | null) => {
        if (date) {
          setSelectedFilterDate(date);
          const newPeriodValue = dateToPeriodValue(date, periodTypeFilter as 'year' | 'month' | 'week' | 'day' || 'day');
          setPeriodValueFilter(newPeriodValue);
        } else {
          setSelectedFilterDate(null);
          setPeriodValueFilter('');
        }
      },
      className: 'period-datepicker',
      dateFormat: '',
      calendarClassName: 'period-calendar',
      isClearable: true,
      placeholderText: 'Chọn giá trị kỳ',
      locale: 'vi',
    };

    const filterType = periodTypeFilter as 'year' | 'month' | 'week' | 'day' || 'day';

    if (filterType === 'year') {
      baseProps.dateFormat = 'yyyy';
      baseProps.showYearDropdown = true;
      baseProps.dropdownMode = 'select';
      baseProps.scrollableYearDropdown = true;
      baseProps.yearDropdownItemNumber = 25;
    } else if (filterType === 'month') {
      baseProps.dateFormat = 'MM/yyyy';
      baseProps.showMonthYearPicker = true;
    } else if (filterType === 'week') {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showWeekNumbers = true;
      baseProps.shouldCloseOnSelect = true;
    } else {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showYearDropdown = true;
      baseProps.scrollableYearDropdown = true;
    }

    return baseProps;
  };

  // DatePicker props for form period value
  const getFormDatePickerProps = () => {
    const formPeriodType = (formData.period_type || 'day') as 'year' | 'month' | 'week' | 'day';
    const formDate = formData.period_value ? periodValueToDate(formData.period_value, formPeriodType) : null;

    const baseProps: any = {
      selected: formDate,
      onChange: (date: Date | null) => {
        if (date) {
          const newPeriodValue = dateToPeriodValue(date, formPeriodType);
          setFormData({ ...formData, period_value: newPeriodValue });
        } else {
          setFormData({ ...formData, period_value: '' });
        }
      },
      className: 'period-datepicker',
      dateFormat: '',
      calendarClassName: 'period-calendar',
      withPortal: true,
      locale: 'vi',
    };

    if (formPeriodType === 'year') {
      baseProps.dateFormat = 'yyyy';
      baseProps.showYearDropdown = true;
      baseProps.dropdownMode = 'select';
      baseProps.scrollableYearDropdown = true;
      baseProps.yearDropdownItemNumber = 25;
    } else if (formPeriodType === 'month') {
      baseProps.dateFormat = 'MM/yyyy';
      baseProps.showMonthYearPicker = true;
    } else if (formPeriodType === 'week') {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showWeekNumbers = true;
      baseProps.shouldCloseOnSelect = true;
    } else {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showYearDropdown = true;
      baseProps.scrollableYearDropdown = true;
    }

    return baseProps;
  };

  // DatePicker props for import modal
  const getImportDatePickerProps = () => {
    const baseProps: any = {
      selected: importSelectedDate,
      onChange: (date: Date | null) => {
        if (date) {
          setImportSelectedDate(date);
          const newPeriodValue = dateToPeriodValue(date, importPeriodType);
          setImportPeriodValue(newPeriodValue);
        } else {
          setImportSelectedDate(null);
          setImportPeriodValue('');
        }
      },
      className: 'period-datepicker',
      dateFormat: '',
      calendarClassName: 'period-calendar',
      withPortal: true,
      locale: 'vi',
    };

    if (importPeriodType === 'year') {
      baseProps.dateFormat = 'yyyy';
      baseProps.showYearDropdown = true;
      baseProps.dropdownMode = 'select';
      baseProps.scrollableYearDropdown = true;
      baseProps.yearDropdownItemNumber = 25;
    } else if (importPeriodType === 'month') {
      baseProps.dateFormat = 'MM/yyyy';
      baseProps.showMonthYearPicker = true;
    } else if (importPeriodType === 'week') {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showWeekNumbers = true;
      baseProps.shouldCloseOnSelect = true;
    } else {
      baseProps.dateFormat = 'dd/MM/yyyy';
      baseProps.showYearDropdown = true;
      baseProps.scrollableYearDropdown = true;
    }

    return baseProps;
  };

  // Update filter date when period value filter or period type filter changes
  useEffect(() => {
    if (periodValueFilter && periodTypeFilter) {
      const date = periodValueToDate(periodValueFilter, periodTypeFilter as 'year' | 'month' | 'week' | 'day');
      setSelectedFilterDate(date);
    } else {
      setSelectedFilterDate(null);
    }
  }, [periodValueFilter, periodTypeFilter]);

  // Reset import date when import period type changes
  useEffect(() => {
    setImportSelectedDate(null);
    setImportPeriodValue('');
  }, [importPeriodType]);

  return (
    <div className="admin-free-keys-page">
      <div className="page-header">
        <h1>Quản lý Chia sẻ Key Free</h1>
        <div className="header-actions">
          {selectedKeys.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger"
              style={{ background: '#dc2626' }}
            >
              Xóa đã chọn ({selectedKeys.length})
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="btn btn-primary"
          >
            + Thêm mới
          </button>
          <label className="btn btn-secondary">
            {importing ? `Đang import... ${importProgress}%` : 'Import Excel'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              disabled={importing}
              style={{ display: 'none' }}
            />
          </label>
          <button onClick={handleExportExcel} className="btn btn-secondary">
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="text"
          placeholder="Tìm kiếm..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="search-input"
        />
        <select
          value={periodTypeFilter}
          onChange={(e) => {
            setPeriodTypeFilter(e.target.value);
            setPeriodValueFilter('');
            setSelectedFilterDate(null);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="">Tất cả loại kỳ</option>
          <option value="year">Năm</option>
          <option value="month">Tháng</option>
          <option value="week">Tuần</option>
          <option value="day">Ngày</option>
        </select>
        {periodTypeFilter ? (
          <div className="calendar-wrapper" style={{ minWidth: '200px' }}>
            <DatePicker {...getFilterDatePickerProps()} />
          </div>
        ) : (
          <input
            type="text"
            placeholder="Chọn loại kỳ trước"
            disabled
            className="filter-select"
          />
        )}
        <select
          value={isActiveFilter}
          onChange={(e) => {
            setIsActiveFilter(e.target.value);
            setPage(1);
          }}
          className="filter-select"
        >
          <option value="">Tất cả</option>
          <option value="true">Đang hiển thị</option>
          <option value="false">Đã ẩn</option>
        </select>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateForm(false);
          resetForm();
        }}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              overflowY: 'auto',
              overflowX: 'visible'
            }}
          >
            <h2>{editingId ? 'Chỉnh sửa' : 'Thêm mới'} Key</h2>
            <form onSubmit={editingId ? handleUpdate : handleCreate}>
              <div className="form-grid">
                <div className="form-group">
                  <label>STT *</label>
                  <input
                    type="number"
                    value={formData.stt || 0}
                    onChange={(e) => setFormData({ ...formData, stt: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quân đoàn</label>
                  <input
                    type="text"
                    value={formData.quan_doan || ''}
                    onChange={(e) => setFormData({ ...formData, quan_doan: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Môn Phái (Ngách)</label>
                  <input
                    type="text"
                    value={formData.mon_phai || ''}
                    onChange={(e) => setFormData({ ...formData, mon_phai: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Từ khóa long mạch (Viral key)</label>
                  <input
                    type="text"
                    value={formData.viral_key || ''}
                    onChange={(e) => setFormData({ ...formData, viral_key: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>RPM</label>
                  <input
                    type="text"
                    value={formData.rpm || ''}
                    onChange={(e) => setFormData({ ...formData, rpm: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Key *</label>
                  <textarea
                    value={formData.key || ''}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    required
                    rows={3}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Prompt</label>
                  <textarea
                    value={formData.prompt || ''}
                    onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                    rows={3}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' }}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Kênh đối thủ</label>
                  <input
                    type="text"
                    value={formData.kenh_doi_thu || ''}
                    onChange={(e) => setFormData({ ...formData, kenh_doi_thu: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Views</label>
                  <input
                    type="number"
                    value={formData.views || 0}
                    onChange={(e) => setFormData({ ...formData, views: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Ngày đăng</label>
                  <input
                    type="date"
                    value={formData.ngay_dang || ''}
                    onChange={(e) => setFormData({ ...formData, ngay_dang: e.target.value })}
                    max="2099-12-31"
                  />
                </div>
                <div className="form-group">
                  <label>Loại kỳ *</label>
                  <select
                    value={formData.period_type || 'year'}
                    onChange={(e) => {
                      const newType = e.target.value as 'year' | 'month' | 'week' | 'day';
                      const currentDate = formData.period_value ? periodValueToDate(formData.period_value, formData.period_type || 'year') : null;
                      if (currentDate) {
                        const newPeriodValue = dateToPeriodValue(currentDate, newType);
                        setFormData({ ...formData, period_type: newType, period_value: newPeriodValue });
                      } else {
                        setFormData({ ...formData, period_type: newType, period_value: '' });
                      }
                    }}
                    required
                  >
                    <option value="year">Năm</option>
                    <option value="month">Tháng</option>
                    <option value="week">Tuần</option>
                    <option value="day">Ngày</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Giá trị kỳ *</label>
                  <div className="calendar-wrapper">
                    <DatePicker {...getFormDatePickerProps()} />
                  </div>
                  {formData.period_value && (
                    <div className="period-value-display" style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                      Giá trị: <strong style={{ color: '#6b7280' }}>{formData.period_value}</strong>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Thứ tự hiển thị</label>
                  <input
                    type="number"
                    value={formData.display_order || 0}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Đang hiển thị
                  </label>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Cập nhật' : 'Tạo mới'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container" ref={tableWrapRef}>
        {loading ? (
          <div className="loading">Đang tải...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    title="Chọn tất cả"
                  />
                </th>
                <th>STT</th>
                <th>Quân đoàn</th>
                <th>Môn Phái</th>
                <th>Viral key</th>
                <th>RPM</th>
                <th>Key</th>
                <th>Views</th>
                <th>Ngày đăng</th>
                <th>Loại kỳ</th>
                <th>Giá trị kỳ</th>
                <th>Prompt</th>
                <th>Kênh đối thủ</th>
                <th>Trạng thái</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className={selectedKeys.includes(key.id!) ? 'selected-row' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(key.id!)}
                      onChange={() => handleSelectKey(key.id!)}
                    />
                  </td>
                  <td>{key.stt}</td>
                  <td>{key.quan_doan || '-'}</td>
                  <td>{key.mon_phai || '-'}</td>
                  <td>{key.viral_key || '-'}</td>
                  <td><StarRating value={key.rpm || 0} /></td>
                  <td className="key-cell" title={key.key}>
                    {key.key.length > 50 ? `${key.key.substring(0, 50)}...` : key.key}
                  </td>
                  <td>{key.views.toLocaleString()}</td>
                  <td>{key.ngay_dang ? new Date(key.ngay_dang).toLocaleDateString('vi-VN') : '-'}</td>
                  <td>
                    {key.period_type === 'year' ? 'Năm' :
                     key.period_type === 'month' ? 'Tháng' :
                     key.period_type === 'week' ? 'Tuần' : 'Ngày'}
                  </td>
                  <td>{key.period_value}</td>
                  <td className="key-cell" title={key.prompt || ''}>
                    {key.prompt ? (key.prompt.length > 50 ? `${key.prompt.substring(0, 50)}...` : key.prompt) : '-'}
                  </td>
                  <td>{key.kenh_doi_thu || '-'}</td>
                  <td>
                    <span className={`badge ${key.is_active ? 'active' : 'inactive'}`}>
                      {key.is_active ? 'Hiển thị' : 'Ẩn'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(key.id!)} className="btn-icon" title="Sửa">
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(key.id!)} className="btn-icon" title="Xóa">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Sticky horizontal scrollbar that follows when scrolling vertically */}
      <div
        ref={stickyScrollbarRef}
        className="sticky-horizontal-scrollbar"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: '16px',
          overflowX: 'scroll',
          overflowY: 'hidden',
          background: '#fff',
          borderTop: '2px solid #888',
          zIndex: 999999,
          display: 'block',
          visibility: 'visible',
          opacity: 1,
          pointerEvents: 'auto',
        }}
      >
        <div style={{ width: Math.max(tableScrollWidth, 2000), height: 1 }} />
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Chọn Loại kỳ và Giá trị kỳ</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Loại kỳ *</label>
                <select
                  value={importPeriodType}
                  onChange={(e) => {
                    setImportPeriodType(e.target.value as any);
                    setImportPeriodValue(''); // Reset khi đổi loại kỳ
                  }}
                  className="filter-select"
                >
                  <option value="year">Năm</option>
                  <option value="month">Tháng</option>
                  <option value="week">Tuần</option>
                  <option value="day">Ngày</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  {importPeriodType === 'year' ? 'Năm *' :
                   importPeriodType === 'month' ? 'Tháng *' :
                   importPeriodType === 'week' ? 'Tuần *' : 'Ngày *'}
                </label>
                <div className="calendar-wrapper">
                  <DatePicker {...getImportDatePickerProps()} />
                </div>
                {importPeriodValue && (
                  <div className="period-value-display" style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                    Giá trị: <strong style={{ color: '#6b7280' }}>{importPeriodValue}</strong>
                  </div>
                )}
              </div>
            </div>
            <div className="form-actions">
              <button onClick={confirmImport} className="btn btn-primary" disabled={!importPeriodValue}>
                Xác nhận Import
              </button>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportPeriodValue('');
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                className="btn btn-secondary"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Trước
          </button>
          <span>
            Trang {page} / {totalPages} (Tổng: {total})
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Sau
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFreeKeysPage;

